// ─── cTrader response parsers (pure TS, no React) ─────────────────

export interface ParsedPosition {
  positionId: number;
  tradeSide: string;
  symbolId?: number;
  symbolName?: string;
  volume: number; // lots (for display)
  volumeUnits: number;
  rawVolume: number; // original cTrader tradeData.volume — use for close/modify
  entryPrice: number;
  currentPrice: number | null;
  sl: number | null;
  tp: number | null;
  commission: number;
  swap: number;
  marginRate: number | null;
  marginMoney: number;
  estimatedMargin: boolean;
  serverProfit: number | null;
  unrealizedPnl: number;
  estimatedPnl: boolean;
  raw: Record<string, unknown>;
  entryIsEstimated: boolean;
}

export interface ParsedReconcileData {
  positions: ParsedPosition[];
  balance: number | null;
  equity: number | null;
  marginUsed: number | null;
  freeMargin: number | null;
  moneyDigits: number;
  raw: Record<string, unknown>;
}

export interface ParsedAccountData {
  balance: number | null;
  equity: number | null;
  margin: number | null;
  freeMargin: number | null;
  marginLevel: number | null;
  depositAsset: string | null;
  leverage: string | null;
  accountId: number | null;
  traderLogin: number | null;
  raw: Record<string, unknown>;
}

export function parseVolume(volumeRaw: unknown): number {
  if (typeof volumeRaw === 'number') return volumeRaw;
  if (typeof volumeRaw === 'string') {
    const n = parseFloat(volumeRaw);
    return isNaN(n) ? 0 : n;
  }
  return 0;
}

/** Try multiple possible field names to extract a numeric value */
function extractNumber(obj: Record<string, unknown>, keys: string[]): number | undefined {
  for (const key of keys) {
    if (typeof obj[key] === 'number') return obj[key] as number;
  }
  return undefined;
}

/** Collect all available keys from an object for debugging */
function getAvailableKeys(obj: Record<string, unknown>): string[] {
  return Object.keys(obj).filter((k) => typeof obj[k] === 'number').sort();
}

export function parsePrice(priceRaw: unknown): number {
  if (typeof priceRaw === 'number') return priceRaw;
  if (typeof priceRaw === 'string') {
    const n = parseFloat(priceRaw);
    return isNaN(n) ? 0 : n;
  }
  return 0;
}

export function parseLeverageNumber(leverageStr: string | null): number | null {
  if (!leverageStr) return null;
  const match = leverageStr.match(/^(\d+):1$/);
  if (match) return parseInt(match[1], 10);
  return null;
}

export function parseReconcileRes(
  raw: Record<string, unknown>,
  fallbackMoneyDigits: number,
  symbolMap: Record<number, string>,
  leverageStr: string | null,
): ParsedReconcileData {
  const moneyDigits = typeof raw.moneyDigits === 'number' ? raw.moneyDigits : fallbackMoneyDigits;
  const divisor = Math.pow(10, moneyDigits);

  // Read top-level margin/equity/freeMargin if broker sends them
  let marginUsed: number | null = typeof raw.marginUsed === 'number' ? raw.marginUsed / divisor : null;

  const result: ParsedReconcileData = {
    positions: [],
    balance: typeof raw.balance === 'number' ? raw.balance / divisor : null,
    equity: typeof raw.equity === 'number' ? raw.equity / divisor : null,
    marginUsed,
    freeMargin: typeof raw.freeMargin === 'number' ? raw.freeMargin / divisor : null,
    moneyDigits,
    raw,
  };

  const posArray = Array.isArray(raw.position) ? raw.position : [];
  let computedMargin = 0;
  let totalUnrealizedPnl = 0;
  const leverageNum = parseLeverageNumber(leverageStr);
  for (const posRaw of posArray) {
    if (typeof posRaw !== 'object' || posRaw === null) continue;
    const p = posRaw as Record<string, unknown>;
    const td = (p.tradeData as Record<string, unknown>) || {};

    const volumeRaw = parseVolume(td.volume);
    // cTrader ProtoOATradeData.volume is in CENTS (e.g. 100,000 units = 10,000,000 cents).
    // Divisor 10_000_000 is CORRECT for converting cents → standard lots (100k units).
    const volumeLots = volumeRaw / 10_000_000;
    const volumeUnits = volumeRaw;

    // cTrader ProtoOATradeData.openPrice is the ENTRY price.
    // p.price in reconcile response is the current market price (used for PnL display),
    // NOT the open price. Use td.openPrice as primary source.
    const entryPrice = typeof td.openPrice === 'number'
      ? td.openPrice
      : parsePrice(p.price);
    console.log(`[parseReconcileRes] positionId=${p.positionId} openPrice(from td)=${td.openPrice} p.price=${p.price} → entryPrice=${entryPrice}`);
    const tradeSideRaw = typeof td.tradeSide === 'number' ? td.tradeSide : 1;
    const tradeSide = tradeSideRaw === 2 ? 'SELL' : 'BUY';
    const symbolId = typeof td.symbolId === 'number' ? td.symbolId : undefined;

    const swap = typeof p.swap === 'number' ? p.swap / divisor : 0;
    const commission = typeof p.commission === 'number' ? p.commission / divisor : 0;

    // Margin: prefer cTrader's usedMargin, fall back to volume * price / leverage
    let usedMarginCents = typeof p.usedMargin === 'number' ? p.usedMargin : 0;
    if (usedMarginCents === 0 && leverageNum && leverageNum > 0 && entryPrice > 0 && volumeUnits > 0) {
      // Forex margin approximation: units * price / leverage
      const approxMargin = (volumeUnits * entryPrice) / leverageNum;
      usedMarginCents = Math.round(approxMargin * divisor);
    }
    const marginMoney = usedMarginCents / divisor;
    const marginRate = typeof p.marginRate === 'number' ? p.marginRate : null;

    // Unrealized PnL: prefer explicit field, then serverProfit
    let positionUnrealizedPnl = 0;
    const rawUnrealizedPnl = typeof p.unrealizedPnl === 'number' ? p.unrealizedPnl : null;
    const rawServerProfit = typeof p.serverProfit === 'number' ? p.serverProfit : null;
    if (rawUnrealizedPnl !== null) {
      positionUnrealizedPnl = rawUnrealizedPnl / divisor;
    } else if (rawServerProfit !== null) {
      positionUnrealizedPnl = rawServerProfit / divisor;
    }
    totalUnrealizedPnl += positionUnrealizedPnl;

    const currentPrice: number | null = null;
    const unrealizedPnl = positionUnrealizedPnl;

    const sl = typeof p.stopLoss === 'number'
      ? p.stopLoss
      : (typeof td.stopLoss === 'number' ? td.stopLoss : null);
    const tp = typeof p.takeProfit === 'number'
      ? p.takeProfit
      : (typeof td.takeProfit === 'number' ? td.takeProfit : null);

    const posMoneyDigits = typeof p.moneyDigits === 'number' ? p.moneyDigits : moneyDigits;

    const position: ParsedPosition = {
      positionId: typeof p.positionId === 'number' ? p.positionId : 0,
      tradeSide,
      symbolId,
      symbolName: symbolId !== undefined ? (symbolMap[symbolId] || 'ID:' + String(symbolId)) : undefined,
      volume: volumeLots,
      volumeUnits,
      entryPrice,
      entryIsEstimated: false,
      currentPrice,
      sl,
      tp,
      commission,
      swap,
      marginRate,
      marginMoney,
      estimatedMargin: usedMarginCents === 0 && leverageNum !== null,
      serverProfit: rawServerProfit !== null ? rawServerProfit / divisor : null,
      unrealizedPnl,
      estimatedPnl: rawUnrealizedPnl === null && rawServerProfit === null,
      raw: { ...p, _parsedMoneyDigits: posMoneyDigits, _computedMarginFallback: usedMarginCents === 0 && leverageNum !== null },
      rawVolume: volumeRaw,
    };
    result.positions.push(position);
    computedMargin += marginMoney;
  }

  // If broker didn't send top-level marginUsed, use computed sum from positions
  if (result.marginUsed === null || result.marginUsed === 0) {
    result.marginUsed = computedMargin;
  }

  // If broker didn't send top-level equity, compute from balance + unrealized PnL
  if (result.equity === null && result.balance !== null) {
    result.equity = result.balance + totalUnrealizedPnl;
  }

  // If broker didn't send top-level freeMargin, compute from equity - marginUsed
  if (result.freeMargin === null && result.equity !== null && result.marginUsed !== null) {
    result.freeMargin = result.equity - result.marginUsed;
  }

  return result;
}

export function parseSymbolRes(raw: Record<string, unknown>): Record<number, string> {
  const map: Record<number, string> = {};
  // cTrader ProtoOA_SYMBOLS_LIST_RES — try multiple possible field names for the symbol array
  const symArray: Array<Record<string, unknown>> = (() => {
    const arr = raw.symbol || raw.symbols || raw.symbolList || raw.archivedSymbol || raw.archivedSymbols;
    if (Array.isArray(arr)) return arr as Array<Record<string, unknown>>;
    // Sometimes the whole payload IS the array
    if (Array.isArray(raw)) return raw as Array<Record<string, unknown>>;
    return [];
  })();
  for (const symRaw of symArray) {
    if (typeof symRaw !== 'object' || symRaw === null) continue;
    const s = symRaw as Record<string, unknown>;
    const sid = typeof s.symbolId === 'number' ? s.symbolId : null;
    const name = typeof s.symbolName === 'string'
      ? s.symbolName
      : typeof s.name === 'string'
        ? s.name
        : typeof s.displayName === 'string' ? s.displayName : null;
    if (sid !== null && name) {
      map[sid] = name;
    }
  }
  return map;
}

export const ASSET_ID_MAP: Record<number, string> = {
  1: 'USD', 2: 'EUR', 3: 'GBP', 4: 'JPY', 5: 'CHF',
  6: 'CAD', 7: 'AUD', 8: 'NZD', 9: 'SGD', 10: 'HKD',
  11: 'CNY', 12: 'MXN', 13: 'ZAR', 14: 'SEK', 15: 'NOK',
  16: 'DKK', 17: 'PLN', 18: 'HUF', 19: 'CZK', 20: 'TRY',
  21: 'RUB', 22: 'ILS', 23: 'KRW', 24: 'TWD', 25: 'THB',
  26: 'IDR', 27: 'INR', 28: 'BRL', 29: 'CLP', 30: 'PEN',
  31: 'COP', 32: 'ARS', 33: 'VEF', 34: 'MYR', 35: 'PHP',
  36: 'AED', 37: 'SAR', 38: 'QAR', 39: 'OMR', 40: 'KWD',
  41: 'BHD', 42: 'JOD', 43: 'EGP', 44: 'NGN', 45: 'GHS',
  46: 'KES', 47: 'UGX', 48: 'TZS', 49: 'ZMW', 50: 'BWP',
  100: 'BTC', 101: 'ETH', 102: 'LTC', 103: 'XRP',
  104: 'BCH', 105: 'USDT',
};

export function formatLeverage(leverageInCents: number | null): string | null {
  if (leverageInCents === null || leverageInCents === undefined || leverageInCents <= 0) return null;
  return (leverageInCents / 100).toFixed(0) + ':1';
}

export function resolveCurrency(trader: Record<string, unknown>): string | null {
  if (typeof trader.depositCurrency === 'string' && trader.depositCurrency) {
    return trader.depositCurrency;
  }
  const assetId = typeof trader.depositAssetId === 'number' ? trader.depositAssetId : null;
  if (assetId !== null && ASSET_ID_MAP[assetId]) {
    return ASSET_ID_MAP[assetId];
  }
  if (assetId !== null) {
    return 'Asset#' + String(assetId);
  }
  return null;
}

export function parseTraderRes(raw: Record<string, unknown>, fallbackAccountId: number): ParsedAccountData {
  const result: ParsedAccountData = {
    balance: null, equity: null, margin: null, freeMargin: null,
    marginLevel: null, depositAsset: null, leverage: null,
    accountId: fallbackAccountId, traderLogin: null, raw,
  };

  const trader = (raw.trader as Record<string, unknown>) || raw;
  const moneyDigits = typeof trader.moneyDigits === 'number' ? trader.moneyDigits : 2;
  const divisor = Math.pow(10, moneyDigits);

  // Balance — try multiple possible field names
  const balRaw = extractNumber(trader, ['balance', 'accountBalance', 'balanceCents', 'balanceInCents']);
  if (balRaw !== undefined) result.balance = balRaw / divisor;

  // Equity
  const eqRaw = extractNumber(trader, ['equity', 'accountEquity', 'equityCents', 'equityInCents']);
  if (eqRaw !== undefined) result.equity = eqRaw / divisor;

  // Margin — cTrader native field is `marginUsed`, but some brokers use different names
  const marginRaw = extractNumber(trader, ['marginUsed', 'usedMargin', 'margin', 'marginUsedCents', 'usedMarginCents']);
  if (marginRaw !== undefined) result.margin = marginRaw / divisor;

  // Free margin
  const freeRaw = extractNumber(trader, ['freeMargin', 'availableMargin', 'freeMarginCents', 'availableMarginCents']);
  if (freeRaw !== undefined) result.freeMargin = freeRaw / divisor;

  result.depositAsset = resolveCurrency(trader);
  if (typeof trader.leverageInCents === 'number') {
    result.leverage = formatLeverage(trader.leverageInCents);
  }
  if (typeof trader.traderLogin === 'number') {
    result.traderLogin = trader.traderLogin;
  }

  // ─── Fallback: read from marginChangedEvent if trader object lacks margin/equity/freeMargin ───
  // cTrader sends margin/equity/freeMargin/marginLevel via ProtoOAMarginChangedEvent (2141),
  // NOT inside ProtoOATraderRes (2122). The gateway now forwards marginEvent in raw._marginEvent.
  const marginEvent = (raw._marginEvent as Record<string, unknown>) || (raw.marginEvent as Record<string, unknown>);
  if (marginEvent) {
    const meDivisor = typeof marginEvent.moneyDigits === 'number'
      ? Math.pow(10, marginEvent.moneyDigits)
      : divisor;

    if (result.margin === null) {
      const meMargin = extractNumber(marginEvent, ['marginUsed', 'usedMargin', 'margin', 'marginUsedCents']);
      if (meMargin !== undefined) result.margin = meMargin / meDivisor;
    }
    if (result.equity === null) {
      const meEquity = extractNumber(marginEvent, ['equity', 'accountEquity', 'equityCents', 'equityInCents']);
      if (meEquity !== undefined) result.equity = meEquity / meDivisor;
    }
    if (result.freeMargin === null) {
      const meFree = extractNumber(marginEvent, ['freeMargin', 'availableMargin', 'freeMarginCents', 'availableMarginCents']);
      if (meFree !== undefined) result.freeMargin = meFree / meDivisor;
    }
    if (result.marginLevel === null) {
      const meMl = extractNumber(marginEvent, ['marginLevel', 'marginLevelCents', 'marginLevelPct']);
      if (meMl !== undefined) {
        let ml = meMl;
        // cTrader may send marginLevel scaled differently by broker.
        // Heuristic: if >1000 it's probably multiplied by 100 (15000 = 150%).
        // If between 1-1000 it's likely already in percent (150 = 150%).
        // If <1 it's probably a decimal (1.5 = 150%).
        if (ml > 1000) ml = ml / 100;
        else if (ml > 0 && ml < 1) ml = ml * 100;
        result.marginLevel = Math.round(ml * 100) / 100;
      }
    }
  }

  // Equity fallback: if still missing, use balance
  if (result.equity === null && result.balance !== null) {
    result.equity = result.balance;
  }
  // Do NOT hard-set margin = 0. Leave it null so caller can fallback to reconcileData.
  if (result.margin === null) {
    result.margin = null;
  }

  // Free margin: only compute if we have real margin. Otherwise leave null for reconcile fallback.
  if (result.freeMargin === null && result.margin !== null && result.margin > 0) {
    if (result.equity !== null) {
      result.freeMargin = result.equity - result.margin;
    } else if (result.balance !== null) {
      result.freeMargin = result.balance - result.margin;
    }
  }

  // Margin Level: only compute if we have real margin > 0
  if (result.marginLevel === null && result.equity !== null && result.margin !== null && result.margin > 0) {
    result.marginLevel = Math.round((result.equity / result.margin) * 10000) / 100;
  }

  // Attach raw cTrader marginLevel to raw for debugging
  const rawMarginLevel = extractNumber(trader, ['marginLevel', 'marginLevelCents', 'marginLevelPct']);
  if (rawMarginLevel !== undefined) {
    (result.raw as Record<string, unknown>)._rawMarginLevel = rawMarginLevel;
  }

  // Debug: store available numeric keys so UI can show them
  (result.raw as Record<string, unknown>)._availableNumericKeys = getAvailableKeys(trader);
  (result.raw as Record<string, unknown>)._moneyDigits = moneyDigits;
  (result.raw as Record<string, unknown>)._divisor = divisor;

  return result;
}

export function formatTimeLeft(expiresAt: string | null): string {
  if (!expiresAt) return 'N/A';
  const remaining = new Date(expiresAt).getTime() - Date.now();
  if (remaining <= 0) return 'Expired';
  const seconds = Math.floor(remaining / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const remainingSeconds = seconds % 60;
  if (hours > 0) return `${hours}h ${remainingMinutes}m ${remainingSeconds}s`;
  if (minutes > 0) return `${minutes}m ${remainingSeconds}s`;
  return `${remainingSeconds}s`;
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '\u2014';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

export function formatCurrency(value: number | null, currency?: string | null): string {
  if (value === null) return '\u2014';
  const num = value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? num + ' ' + currency : num;
}
